import { Link } from "react-router-dom";
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaPredicate } from 'react-media-hook';

import { get } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';
import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';
import { getEmailChangeSummary, getEmailDataSet, getEmailHeadline } from 'utils/sentEmailUtils';

import DateFormatter from 'formatters/DateFormatter';
import Spinner from 'components/Spinner';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

const PER_PAGE = 10;
const SEARCH_DELAY_MS = 300;

const ROW_GRID = 'tw-grid tw-grid-cols-[36px_1.3fr_2fr_130px_120px] tw-items-center tw-gap-3';

// Button for opening the record an alert / notification was about
function RecordDetailsButton({ email, className, children }) {
	const dispatch = useDispatch();
	const dataSet = getEmailDataSet(email?.class);
	if (!dataSet || !email?.item_id) {
		return <span className={className} aria-hidden="true">-</span>;
	}
	return (
		<button
			type="button"
			className={`${className} btn-hyperlink`}
			aria-label={`View ${getEmailHeadline(email)} details`}
			onClick={() => dispatch(openModal(MODAL_TYPES.RECORD_DETAILS, { dataSet, id: email.item_id }))}
		>
			{children || <InfoIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />}
		</button>
	);
}

function PropertyLink({ email }) {
	const address = email?.property?.address;
	if (!address) {
		return '-';
	}
	return <Link className="tw-text-text-primary hover:tw-underline" to={`/property/${address}`}>{address}</Link>;
}

function EmailSummary({ email }) {
	const changes = getEmailChangeSummary(email?.change_data);
	return (
		<div className="tw-min-w-0">
			<div className="tw-truncate tw-text-xs tw-font-medium tw-text-text-primary">{getEmailHeadline(email)}</div>
			{changes && <div className="tw-truncate tw-text-xs tw-text-text-secondary" title={changes}>{changes}</div>}
		</div>
	);
}

const AlertsAndNotificationsTable = ({ title, perPage = PER_PAGE, hidePaginator = false, showSearch = true }) => {
	const dispatch = useDispatch();
	const isMobile = useMediaPredicate(MOBILE_SIZE);
	const { userId, authToken } = useSelector((state) => ({
		userId: state.user.userData?.id,
		authToken: state.user.authToken,
	}));

	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [result, setResult] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);

	// search on the server once the user stops typing
	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(1);
		}, SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [searchInput]);

	useEffect(() => {
		if (!userId) {
			return undefined;
		}
		let isCurrent = true;
		const params = new URLSearchParams({ page, per_page: perPage });
		if (search) {
			params.set('search', search);
		}
		setIsLoading(true);
		setHasError(false);
		get({ url: `${getApiUrl()}/api/users/${userId}/sent-emails?${params.toString()}`, authToken, dispatch })
			.then((response) => {
				if (isCurrent) setResult(response);
			})
			.catch(() => {
				if (isCurrent) {
					setResult(null);
					setHasError(true);
				}
			})
			.finally(() => {
				if (isCurrent) setIsLoading(false);
			});
		return () => {
			isCurrent = false;
		};
	}, [userId, authToken, page, perPage, search, dispatch]);

	const rows = result?.data || [];
	const emptyText = hasError
		? 'Could not load alerts and notifications. Please try again.'
		: 'No alerts or notifications';

	return (
		<div className="violerts-v3-root tw-bg-[#F7F7F7] tw-border tw-border-border-01 tw-rounded-[8px] tw-p-5">
			{(title || showSearch) && (
				<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-mb-4">
					{title ? <h1 className="tw-text-xl tw-font-bold tw-text-text-primary tw-m-0">{title}</h1> : <div />}
					{showSearch && (
						<div className="tw-relative tw-w-64">
							<SearchIcon className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-text-secondary" />
							<input
								type="text"
								placeholder="Search"
								aria-label="Search alerts and notifications"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								className="tw-w-full tw-rounded-[4px] tw-border tw-border-border-01 tw-bg-white tw-py-1.5 tw-pl-9 tw-pr-3 tw-text-xs tw-text-text-primary placeholder:tw-text-text-placeholder focus:tw-border-border-focus focus:tw-outline-none"
							/>
						</div>
					)}
				</div>
			)}

			{isLoading ? (
				<div className="tw-flex tw-justify-center tw-py-12"><Spinner /></div>
			) : (
				<div className="tw-flex tw-flex-col tw-gap-2">
					{isMobile ? (
						<div className="tw-flex tw-flex-col tw-gap-3">
							{!rows.length && <div className="tw-py-8 tw-text-center tw-text-sm tw-text-text-secondary">{emptyText}</div>}
							{rows.map((email) => (
								<div key={email.id} className="tw-flex tw-flex-col tw-gap-2 tw-rounded-[8px] tw-border tw-border-border-02 tw-bg-surface-layer-01 tw-p-4">
									<div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
										<span className="tw-text-xs tw-text-text-secondary"><DateFormatter text={email.sent_at || email.created_at} /></span>
										<span className="tw-text-xs tw-font-medium tw-text-text-secondary">{email.type === 'notification' ? 'Notification' : 'Alert'}</span>
									</div>
									<div className="tw-text-sm tw-font-bold"><PropertyLink email={email} /></div>
									<div className="tw-flex tw-items-start tw-gap-2">
										<RecordDetailsButton email={email} className="tw-text-text-secondary" />
										<EmailSummary email={email} />
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="tw-overflow-hidden tw-rounded-[4px]">
							<div className={`${ROW_GRID} tw-rounded-[4px] tw-bg-[#E3E3E3] tw-px-5 tw-py-1.5 tw-text-xs tw-font-bold tw-text-text-primary`}>
								<span aria-hidden="true" />
								<span>Property</span>
								<span>Notification</span>
								<span>Type</span>
								<span>Date</span>
							</div>
							{!rows.length && <div className="tw-py-12 tw-text-center tw-text-sm tw-text-text-secondary">{emptyText}</div>}
							{rows.map((email) => (
								<div key={email.id} className={`${ROW_GRID} tw-border-b tw-border-border-01 tw-px-5 tw-py-1.5 last:tw-border-b-0 hover:tw-bg-[#EDEDED] tw-transition-colors`}>
									<RecordDetailsButton
										email={email}
										className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-text-secondary hover:tw-bg-surface-layer-02 hover:tw-text-text-primary"
									/>
									<div className="tw-truncate tw-text-xs tw-font-medium"><PropertyLink email={email} /></div>
									<EmailSummary email={email} />
									<div className="tw-truncate tw-text-xs tw-text-text-secondary">{email.type === 'notification' ? 'Notification' : 'Alert'}</div>
									<div className="tw-whitespace-nowrap tw-text-xs tw-text-text-secondary"><DateFormatter text={email.sent_at || email.created_at} /></div>
								</div>
							))}
						</div>
					)}
					{result?.last_page > 1 && !hidePaginator && (
						<div className="tw-flex tw-justify-center tw-pt-4">
							<PaginatorV3 currentPage={result.current_page} totalPages={result.last_page} onPageChange={setPage} />
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AlertsAndNotificationsTable;
